"use client"

import Link from "next/link"
import { ArrowRight } from "lucide-react"

import { useImpressionEvent } from "@/hooks/useImpressionEvent"
import { analytics, trackEvent } from "@/lib/analytics"
import type { WeekStripData } from "@/lib/saved-result-week-strip"

const CTA_LOCATION = "saved_result_week_strip"

/**
 * WS6 V1 quiet ribbon: one line + one link under the saved-result summary,
 * fed server-side by buildSavedResultWeekStrip (which returns null when no
 * campaign window is open, so this component never renders an empty state).
 *
 * Analytics contract: cta_surface_shown fires once when the ribbon scrolls
 * into view, cta_clicked once per link press, both on the same cta_location
 * with campaign_id + cta_variant so CTR is computable per campaign.
 */
export function SavedResultWeekStrip({ strip }: { strip: WeekStripData }) {
  const impressionRef = useImpressionEvent<HTMLDivElement>(
    CTA_LOCATION,
    { campaign_id: strip.campaignId, cta_variant: "v1_ribbon" },
    { emit: analytics.ctaSurfaceShown },
  )

  return (
    <div
      ref={impressionRef}
      className="mt-6 md:mt-8 flex flex-wrap items-center gap-3 rounded-xl border border-green-100 bg-green-50/70 px-4 py-3"
    >
      <span aria-hidden className="h-2 w-2 flex-none rounded-full bg-green-600" />
      <p className="flex-1 basis-64 text-sm text-gray-700 leading-relaxed">
        <span className="font-semibold text-gray-900">{strip.headline}</span>{" "}
        {strip.message}
      </p>
      <Link
        href={strip.toolHref}
        onClick={() =>
          trackEvent("cta_clicked", {
            cta_location: CTA_LOCATION,
            campaign_id: strip.campaignId,
            cta_variant: "v1_ribbon",
            destination: strip.toolHref,
          })
        }
        className="inline-flex items-center gap-1 whitespace-nowrap text-sm font-semibold text-green-700 hover:text-green-800 transition-colors"
      >
        See your timing
        <ArrowRight className="h-3.5 w-3.5" />
      </Link>
    </div>
  )
}
